import { markSent, pendingLogs } from '../persistence/examLog';
import type { Store } from '../persistence/port';
import type { ExamLogRow } from '../persistence/rows';
import { EXAM_LOG_TYPES } from '../persistence/seed';
import { lohntWiederholung, type ServerClient } from './client';
import { istNetzausfall } from './verbindung';

/**
 * Der Rückstand geht hinaus.
 *
 * Die App sammelt ihre Prüfungsereignisse in `ExamLog`; `transferred === 'N'` heisst „noch nicht
 * übertragen" (das ist der Ausgangspuffer, keine eigene Tabelle, s. Spec §13). Dieses Modul
 * schickt sie an `POST /rest/v1/exam-log/append` und stempelt, was angekommen ist.
 *
 * **Der Vertrag des Servers bestimmt den Ablauf** (Relay-`README.md`):
 *
 * - Die Antwort zählt `accepted`, `lostAndFound` und listet `rejected`.
 * - **Wiederholt wird nur, was in `rejected` steht.** Alles andere hat der Server behalten;
 *   ein zweiter Anlauf legte dort nur weitere Zeilen an.
 * - **Dubletten sind ausdrücklich zugelassen**, der Server vergibt die Schlüssel selbst.
 *   Unsere `id` ist nur die Marke, an der ein abgelehnter Eintrag benannt wird.
 * - Ein Stapel geht **teilweise** durch; ein kaputter Eintrag sperrt das Gerät nicht aus.
 */

/** Ein Eintrag, wie ihn Relay erwartet. */
interface Eintrag {
  id: string;
  examLogTypeName: string;
  examID: string | null;
  candidateID: string | null;
  examinerID: string | null;
  actorID: string | null;
  referenceType: string | null;
  referenceIdentifier: string | null;
  value: string;
  creationDate: string;
  pointsNumerator: number | null;
  pointsDenominator: number | null;
}

export interface AppendAntwort {
  accepted: number;
  lostAndFound: number;
  /** Kennungen (oder `#Position`) der Einträge, die erneut geschickt werden sollen. */
  rejected: string[];
}

export interface SyncDeps {
  store: Store;
  client: ServerClient;
  /**
   * Das Dateilog. `stufe` bestimmt, unter welcher Konsolenfunktion die Zeile am Kabel in
   * Xcode erscheint (s. `platform/appLog.ts`); in der Datei sieht jede Zeile gleich aus.
   */
  log: (text: string, stufe?: 'warn' | 'error') => Promise<void>;
  /** Wie viele Einträge je Aufruf. Relay nimmt Stapel; 200 ist der Vorschlag aus dem Plan. */
  stapelGroesse?: number;
}

export interface SyncErgebnis {
  /** Vom Server behalten und lokal gestempelt. */
  uebertragen: number;
  /** Beim Server im Fundbüro gelandet — erledigt, aber ein Fehler bei uns. */
  fundbuero: number;
  /** Bleibt liegen und wird beim nächsten Lauf erneut versucht. */
  liegengeblieben: number;
  /** Wie viele Stapel gegangen sind. */
  stapel: number;
  /**
   * Zeilen einer **Simulationsprüfung**: örtlich abgehakt, ohne dass etwas gesendet wurde.
   *
   * Eigenes Feld und nicht unter `uebertragen`: dort stünde eine Zahl, die eine Übertragung
   * behauptet, die nie stattgefunden hat.
   */
  simuliert: number;
  /**
   * Der Server war nicht zu erreichen. Der Takt bricht seinen Durchgang dann ab: was danach
   * käme (Abgleich, Dateien), scheiterte am selben Grund (2026-10-02).
   */
  netzausfall?: boolean;
}

const NAME_ZU_ID = new Map(EXAM_LOG_TYPES.map((t) => [t.id, t.name]));

/**
 * Eine Zeile der Ablage in einen Eintrag für Relay übersetzen.
 *
 * ⚠️ **Relay will den Namen des Typs, nicht seine Kennung.** Die Kennung ist zwar auf beiden
 * Seiten dieselbe (`uuidv5` über den Namen), aber der Vertrag nennt `examLogTypeName` — und
 * eine Kennung, die der Server nicht auflöst, landet stillschweigend im Fundbüro.
 *
 * `deviceIdentifier`, `deviceName` und `storeDate` werden **nicht** mitgeschickt: die setzt der
 * Server aus der Sitzung und ignoriert, was im Rumpf steht.
 */
export function zuEintrag(zeile: ExamLogRow): Eintrag | null {
  const name = NAME_ZU_ID.get(zeile.examLogTypeID);
  if (!name) return null;
  return {
    id: zeile.id,
    examLogTypeName: name,
    examID: zeile.examID,
    candidateID: zeile.candidateID,
    examinerID: zeile.examinerID,
    actorID: zeile.actorID,
    referenceType: zeile.referenceType,
    referenceIdentifier: zeile.referenceIdentifier,
    value: zeile.value,
    creationDate: zeile.creationDate,
    pointsNumerator: zeile.pointsNumerator,
    pointsDenominator: zeile.pointsDenominator,
  };
}

/**
 * Ein einziger Durchlauf über den Rückstand. Läuft, bis nichts Sendbares mehr liegt oder ein
 * Stapel scheitert.
 *
 * Der Aufrufer entscheidet über den Takt; dieses Modul kennt keinen Zeitgeber. So lässt es sich
 * prüfen, ohne auf Uhren zu warten, und die Oberfläche kann dieselbe Funktion für „Jetzt
 * übertragen" benutzen.
 */
/**
 * Die Kennungen aller Simulationsprüfungen auf dem Gerät.
 *
 * Als Menge und nicht je Zeile abgefragt: der Rückstand kann tausende Zeilen tragen, die
 * Prüfungen auf einem Gerät sind eine Handvoll.
 */
async function simulativeExams(store: SyncDeps['store']): Promise<Set<string>> {
  const alle = await store.all<{ id: string; simulative?: string }>('Exam');
  return new Set(alle.filter((e) => e.simulative === 'Y').map((e) => e.id));
}

export async function uebertrageOffen(deps: SyncDeps): Promise<SyncErgebnis> {
  const groesse = deps.stapelGroesse ?? 200;
  const ergebnis: SyncErgebnis = { uebertragen: 0, fundbuero: 0, liegengeblieben: 0, stapel: 0, simuliert: 0 };

  const alleOffen = await pendingLogs(deps.store);
  if (alleOffen.length === 0) return ergebnis;

  /**
   * ⛔ **Zeilen einer Simulationsprüfung gehen gar nicht los** (Entscheidung des Nutzers,
   * 2026-09-18).
   *
   * ⬅️ **Richtigstellung vom 2026-09-18:** hier stand „der Server nähme sie an und verwürfe
   * sie". Das galt einen halben Tag. Die Relay-Seite hat den Wächter auf Bitte ihres Nutzers
   * **wieder entfernt** — er war doppelte Arbeit, solange die Apps ohnehin nichts schicken.
   *
   * ⛔ **Damit ist diese Sperre nicht Sparsamkeit, sondern das einzige Schutznetz.** Ginge eine
   * Zeile hinaus, würde sie am Server **gespeichert** wie bei jeder echten Prüfung — und
   * niemand sähe einer Übungsbewertung an, dass sie geübt war. Wer diese Sperre entfernt,
   * schreibt Schulungsdaten in die echte Prüfungsdatenbank.
   *
   * ⚠️ **Sie werden örtlich abgehakt, nicht liegengelassen.** Sonst wüchse der Rückstand ewig,
   * und „N Einträge warten auf Übertragung" stünde dauerhaft am Prüfer — eine Zahl, die nie
   * kleiner wird, liest niemand mehr.
   *
   * ⚠️ **Abhaken heisst hier verwerfen, und das ist unumkehrbar.** Wird dieselbe Prüfung am
   * Server später auf „echt" gestellt, gehen die bereits abgehakten Zeilen **nicht** nachträglich
   * hinaus. Deshalb die Zeile im Dateilog: ohne sie liesse sich hinterher nicht belegen, warum
   * für diese Station nichts angekommen ist.
   */
  const simulativ = await simulativeExams(deps.store);
  const simuliert = alleOffen.filter((z) => z.examID !== null && simulativ.has(z.examID));
  if (simuliert.length > 0) {
    await markSent(deps.store, simuliert.map((z) => z.id));
    ergebnis.simuliert = simuliert.length;
    await deps.log(
      'Simulationsprüfung: ' + String(simuliert.length) +
        ' Protokollzeilen örtlich abgehakt, nichts gesendet (nichts wird aufgezeichnet).',
    );
  }

  const offen = alleOffen.filter((z) => z.examID === null || !simulativ.has(z.examID));
  if (offen.length === 0) return ergebnis;

  /**
   * Zeilen, deren Typ wir nicht auflösen können, gehen gar nicht erst los. Sie blieben sonst
   * für immer im Rückstand und würden bei jedem Durchlauf erneut geschickt.
   */
  const unbekannt = offen.filter((z) => !NAME_ZU_ID.has(z.examLogTypeID));
  if (unbekannt.length > 0) {
    await deps.log(
      'Übertragung: ' + String(unbekannt.length) + ' Einträge mit unbekanntem Typ bleiben liegen (' +
        [...new Set(unbekannt.map((z) => z.examLogTypeID))].join(', ') + ')',
      'warn',
    );
    ergebnis.liegengeblieben += unbekannt.length;
  }

  const sendbar = offen.filter((z) => NAME_ZU_ID.has(z.examLogTypeID));

  for (let i = 0; i < sendbar.length; i += groesse) {
    const stapel = sendbar.slice(i, i + groesse);
    const entries = stapel.map(zuEintrag).filter((e): e is Eintrag => e !== null);

    let antwort: AppendAntwort;
    try {
      antwort = await deps.client.post<AppendAntwort>('/rest/v1/exam-log/append', { entries });
    } catch (fehler) {
      // Ein gescheiterter Stapel bricht den Lauf ab: was danach käme, scheiterte am selben
      // Grund. Liegengeblieben ist alles ab hier — beim nächsten Takt von vorn.
      ergebnis.liegengeblieben += sendbar.length - i;
      // Kein Netz: das steht schon einmal je Offline-Phase im Log (`verbindung.ts`).
      if (istNetzausfall(fehler)) {
        ergebnis.netzausfall = true;
        return ergebnis;
      }
      await deps.log(
        'Übertragung abgebrochen nach ' + String(ergebnis.stapel) + ' Stapeln: ' + String(fehler) +
          (lohntWiederholung(fehler) ? ' — wird erneut versucht' : ' — braucht eine Entscheidung'),
        lohntWiederholung(fehler) ? 'warn' : 'error',
      );
      return ergebnis;
    }

    ergebnis.stapel += 1;

    const abgelehnt = new Set(antwort.rejected ?? []);
    // Der Server darf `#Position` statt einer Kennung nennen, wenn keine mitkam. Unsere Zeilen
    // tragen immer eine — die Position wird trotzdem berücksichtigt, damit ein Eintrag nicht
    // fälschlich als angekommen gilt.
    const gestempelt = stapel.filter((z, pos) => !abgelehnt.has(z.id) && !abgelehnt.has('#' + String(pos)));

    await markSent(deps.store, gestempelt.map((z) => z.id));

    ergebnis.uebertragen += gestempelt.length;
    ergebnis.fundbuero += antwort.lostAndFound ?? 0;
    ergebnis.liegengeblieben += stapel.length - gestempelt.length;

    if ((antwort.lostAndFound ?? 0) > 0) {
      /*
       * **Nicht stillschweigend abhaken.** Der Server hat sie behalten, aber eine Zeile im
       * Fundbüro ist ein Fehler auf unserer Seite — ein krummes Datum, ein unbekannter Typ,
       * ein Verweis ins Leere. Wer das nicht protokolliert, erfährt es nie.
       *
       * ⚠️ **Es gibt dort keinen Rückweg** (am 2026-09-14 von der Relay-Serversitzung im
       * Serverquelltext nachgesehen): `LostAndFoundExamLog` hat keine Route, keine Ansicht und
       * kein Wiedereinspielen — der Server schreibt hinein und liest nie wieder heraus. Eine
       * Zeile dort zählt in **keiner Auswertung** mit, und sie herauszuholen braucht einen
       * Menschen mit Datenbankzugang, der überhaupt erst erfahren muss, dass dort etwas liegt.
       *
       * Diese Zeile im Dateilog ist heute die einzige Spur. Deshalb nennt sie beides: dass es
       * angekommen ist, und dass „angekommen" hier nicht „gespeichert" heisst.
       *
       * ⚠️ **Gestempelt wird trotzdem** (die Zeilen stehen nicht in `rejected`), und das bleibt
       * richtig: erneutes Senden legte sie nur wieder ins Fundbüro. Einen Rückstand zu pflegen
       * für einen Weg, den es nicht gibt, hülfe niemandem.
       */
      await deps.log(
        'Übertragung: ' + String(antwort.lostAndFound) + ' Einträge liegen beim Server im ' +
          'Fundbüro (LostAndFoundExamLog) — angekommen, aber NICHT ausgewertet. Sie zählen in ' +
          'keiner Auswertung mit und es gibt keinen automatischen Rückweg: das muss jemand am ' +
          'Server von Hand ansehen.',
        'error',
      );
    }
    if (abgelehnt.size > 0) {
      await deps.log(
        'Übertragung: ' + String(abgelehnt.size) + ' Einträge abgelehnt (vorübergehend), ' +
          'bleiben im Rückstand.',
        'warn',
      );
    }
  }

  return ergebnis;
}

/**
 * Der laufende Durchgang — **modulweit**, nicht je `createSync`.
 *
 * ⚠️ **Der Takt und „Jetzt übertragen" sind zwei Aufrufer, nicht einer.** Die Oberfläche legt
 * bei jedem Tippen ein neues `createSync` an; ein Merker im Abschluss hätte also nur gegen sich
 * selbst geschützt, und gegen den Takt (`server/takt.ts`) gar nicht. Zwei Läufe übereinander
 * lesen denselben Rückstand — `transferred` wird erst nach der Antwort gesetzt — und schicken
 * jede Zeile doppelt. Relay lässt Dubletten ausdrücklich zu und vergibt die Schlüssel selbst;
 * sie fielen also nirgends auf und stünden in der Auswertung zweimal.
 *
 * Der zweite Aufrufer bekommt das Ergebnis des laufenden — dieselbe Bauart wie die
 * `läuft`-Kette in `autosave.ts`.
 */
let laeuft: Promise<SyncErgebnis> | null = null;

/** Dieselbe Übertragung, aber nie zweimal gleichzeitig. */
export function createSync(deps: SyncDeps) {
  return {
    async jetztUebertragen(): Promise<SyncErgebnis> {
      if (laeuft) return laeuft;
      laeuft = uebertrageOffen(deps).finally(() => {
        laeuft = null;
      });
      return laeuft;
    },
    laeuftGerade: () => laeuft !== null,
  };
}
